import { formatDateWithDynamicTimezone } from "@marketplace/shared/utils";
import { ConditionalRendering, Loader, Text } from "@marketplace/ui";
import { useTranslation } from "react-i18next";

import type { ClaimTrackingStatusItem } from "../../dtos/get-claim-tracking-status-response-dto";
import type { ErfinaInvoiceStatusItem } from "../../dtos/get-erfina-invoice-status-response-dto";
import { useGetClaimStatusTrackingQuery } from "../../hooks/queries/use-get-claim-status-tracking";
import { useGetErfinaInvoiceStatusQuery } from "../../hooks/queries/use-get-erfina-invoice-status-query";
import { StatusTrackerItem } from "./status-tracker-item";

type ClaimOrderStatusTrackerProps = {
  publicId?: string | null;
  rejectReason?: string | null;
  currentStatus?: string | null;
};

export const ClaimOrderStatusTracker = ({
  publicId,
  rejectReason,
  currentStatus
}: ClaimOrderStatusTrackerProps) => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimHistoryPage"
  });
  const { data: statusTrackingData, isLoading: isLoadingTracking } =
    useGetClaimStatusTrackingQuery({
      queryParams: { publicId: publicId! },
      options: {
        enabled: !!publicId
      }
    });
  const { data: erfinaStatusData, isLoading: isLoadingErfina } =
    useGetErfinaInvoiceStatusQuery({
      queryParams: { publicClaimId: publicId! },
      options: {
        enabled: !!publicId
      }
    });

  const hasRejectStatus = statusTrackingData?.data?.some(
    (d: ClaimTrackingStatusItem) => d.status === "reject"
  );

  const isCurrentStatusReject = currentStatus === "reject";
  const hasRejectReason = !!rejectReason && rejectReason.trim() !== "";

  const statusFlow = hasRejectStatus
    ? [
        {
          key: "review",
          label: t("statusTracking.statusLabel.onReview"),
          row: 1,
          col: 1
        },
        {
          key: "reject",
          label: t("statusTracking.statusLabel.claimReject"),
          row: 1,
          col: 2
        },
        {
          key: "approve",
          label: t("statusTracking.statusLabel.claimApprove"),
          row: 1,
          col: 3
        },
        {
          key: "payment_open",
          label: t("statusTracking.statusLabel.onPayment"),
          row: 2,
          col: 1
        },
        {
          key: "payment_processing",
          label: t("statusTracking.statusLabel.onPaymentProcessing"),
          row: 2,
          col: 2
        },
        {
          key: "payment_success",
          label: t("statusTracking.statusLabel.onPaymentSuccess"),
          row: 2,
          col: 3
        }
      ]
    : [
        {
          key: "review",
          label: t("statusTracking.statusLabel.onReview"),
          row: 1,
          col: 1
        },
        {
          key: "approve",
          label: t("statusTracking.statusLabel.claimApprove"),
          row: 1,
          col: 2
        },
        {
          key: "payment_open",
          label: t("statusTracking.statusLabel.onPayment"),
          row: 1,
          col: 3
        },
        {
          key: "payment_processing",
          label: t("statusTracking.statusLabel.onPaymentProcessing"),
          row: 2,
          col: 1
        },
        {
          key: "payment_success",
          label: t("statusTracking.statusLabel.onPaymentSuccess"),
          row: 2,
          col: 2
        }
      ];

  const furthestIndex = Math.max(
    -1,
    ...(statusTrackingData?.data ?? []).map((d) =>
      statusFlow.findIndex((s) => s.key === d.status)
    )
  );

  const isStatusCompleted = (index: number): boolean => {
    return index <= furthestIndex;
  };

  const getStatusDate = (statusKey: string): string => {
    const statusData = statusTrackingData?.data.find(
      (d: ClaimTrackingStatusItem) => d.status === statusKey
    );
    return statusData
      ? formatDateWithDynamicTimezone(statusData.created_at)
      : "";
  };

  // Get top and bottom row statuses
  const topRowStatuses = statusFlow.filter((s) => s.row === 1);
  const bottomRowStatuses = statusFlow.filter((s) => s.row === 2);

  // Erfina invoice status flow
  const erfinaStatusFlow = [
    {
      key: "invoice_validated",
      label: t("erfinaStatus.statusLabel.invoiceValidated")
    },
    {
      key: "pr_created_status",
      label: t("erfinaStatus.statusLabel.prCreated")
    },
    {
      key: "payment_created_status",
      label: t("erfinaStatus.statusLabel.paymentCreated")
    }
  ];

  const getErfinaStatusDate = (statusKey: string): string => {
    const erfinaData = erfinaStatusData?.data;
    if (!erfinaData) return "";

    const statusItem = erfinaData[statusKey as keyof typeof erfinaData] as
      | ErfinaInvoiceStatusItem
      | undefined;

    return statusItem?.created_at
      ? formatDateWithDynamicTimezone(statusItem.created_at)
      : "";
  };

  const isErfinaStatusCompleted = (statusKey: string): boolean => {
    const erfinaData = erfinaStatusData?.data;
    if (!erfinaData) return false;

    const statusItem = erfinaData[statusKey as keyof typeof erfinaData] as
      | ErfinaInvoiceStatusItem
      | undefined;

    return statusItem?.status === "Yes";
  };

  const isLoading = isLoadingTracking || isLoadingErfina;

  return (
    <>
      <ConditionalRendering condition={isLoading}>
        <div className="text-fjord-grey-500 text-center py-8">
          <Loader />
        </div>
      </ConditionalRendering>
      <ConditionalRendering condition={!isLoading}>
        {/* Reject description */}
        <div className="w-full space-y-6">
          <ConditionalRendering
            condition={isCurrentStatusReject && hasRejectReason}
          >
            <div className="flex flex-col gap-2 ">
              <Text className="text-fjord-grey-500">
                {t("statusTracking.rejectDescription")}
              </Text>
              <div className="rounded-lg bg-mischka-100 border border-solid border-mischka-400 p-3">
                <Text size="xl" className="text-fjord-grey-500">
                  {rejectReason}
                </Text>
              </div>
            </div>
            <hr className="h-px border-0 w-full bg-mischka-400" />
          </ConditionalRendering>
          {/* Tracking Status */}
          <div>
            <Text className="text-fjord-grey-500 mb-2">
              {t("statusTracking.title")}
            </Text>

            <div className="relative">
              {/* Grid container */}
              <div className="flex flex-wrap justify-center items-center gap-y-4">
                {/* Row 1 - Top 3 statuses (always 3) */}
                {topRowStatuses.map((status, idx) => (
                  <StatusTrackerItem
                    key={status.key}
                    status={status}
                    isCompleted={isStatusCompleted(
                      statusFlow.findIndex((s) => s.key === status.key)
                    )}
                    getStatusDate={getStatusDate}
                    showConnector={idx < topRowStatuses.length - 1}
                  />
                ))}

                {/* Row 2 - Bottom statuses */}
                {bottomRowStatuses.map((status, idx) => (
                  <StatusTrackerItem
                    key={status.key}
                    status={status}
                    isCompleted={isStatusCompleted(
                      statusFlow.findIndex((s) => s.key === status.key)
                    )}
                    getStatusDate={getStatusDate}
                    showConnector={idx < bottomRowStatuses.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Erfina Invoice Status */}
          <ConditionalRendering condition={!!publicId}>
            <div>
              <Text className="text-fjord-grey-500 mb-2">
                {t("erfinaStatus.title")}
              </Text>

              <div className="relative">
                <div className="flex flex-wrap justify-center items-center gap-y-4">
                  {erfinaStatusFlow.map((status, idx) => (
                    <StatusTrackerItem
                      key={status.key}
                      status={status}
                      isCompleted={isErfinaStatusCompleted(status.key)}
                      getStatusDate={getErfinaStatusDate}
                      showConnector={idx < erfinaStatusFlow.length - 1}
                    />
                  ))}
                </div>
              </div>
            </div>
          </ConditionalRendering>
        </div>

        <hr className="h-px border-0 w-full bg-mischka-400 mt-4" />
      </ConditionalRendering>
    </>
  );
};
